<template>
  <div v-loading="loading" class="live-detail-page">
    <p v-if="error" class="err">{{ error }}</p>
    <template v-else>
      <KpiBoard :items="kpiItems" :columns="6" class="live-detail-page__kpis" />
      <Crud
        class="list-page-crud"
        :filter-items="filterItems"
        :value="filters"
        :pagination="pagination"
        @input="onFiltersInput"
      >
        <template #table>
          <Table
            :columns="tableColumns"
            :data="tableRows"
            row-key="id"
            border
            height="100%"
            :on-row-click="openRow"
          />
        </template>
      </Crud>
      <LiveCaseDrawer
        :visible="drawerOpen"
        :row="activeRow"
        :option-cases="rows"
        @close="drawerOpen = false"
        @saved="onSaved"
      />
    </template>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

import { fetchLiveCases, initialLiveCasesLoadState, peekLiveCasesCache, type LiveCase } from '@/api/live-case';
import { Crud, Table } from '@/components/crud';
import type { FilterItem } from '@/components/crud/types';
import { KpiBoard, LiveCaseDrawer } from '@/components';
import type { KpiItem } from '@/components/kpi-board/kpi-board.vue';
import { summarizeLive, type LiveRate, type LiveSummary } from '@/domain/live-case';

import { liveDetailColumns } from './columns';

function filterLiveRows(rows: LiveCase[], keyword: string): LiveCase[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) =>
    [row.pm, row.lh, row.yc, row.fzr].join(' ').toLowerCase().includes(q),
  );
}

function formatLiveRateText(rate: LiveRate): string {
  if (rate.percent == null) return '—';
  return `${rate.percent}%`;
}

interface LiveDetailFilters {
  q: string;
}

export default Vue.extend({
  name: 'LiveDetail',
  components: { Crud, KpiBoard, LiveCaseDrawer, Table },
  data() {
    const initial = initialLiveCasesLoadState();
    return {
      loading: initial.loading,
      error: '',
      rows: initial.rows,
      filters: { q: '' } as LiveDetailFilters,
      page: 1,
      pageSize: 50,
      drawerOpen: false,
      activeRow: null as LiveCase | null,
    };
  },
  computed: {
    filterItems(): FilterItem[] {
      return [
        {
          key: 'q',
          type: 'input',
          placeholder: '筛选品名、料号、异常、负责人',
          width: '280px',
        },
      ];
    },
    filteredRows(): LiveCase[] {
      return filterLiveRows(this.rows, this.filters.q);
    },
    tableRows(): LiveCase[] {
      const start = (this.page - 1) * this.pageSize;
      return this.filteredRows.slice(start, start + this.pageSize);
    },
    tableColumns() {
      return liveDetailColumns;
    },
    summary(): LiveSummary {
      return summarizeLive(this.filteredRows);
    },
    kpiItems(): KpiItem[] {
      const summary = this.summary;
      return [
        { value: summary.unreplied, label: '未回复', icon: 'el-icon-message', tone: 'danger' },
        { value: summary.handling, label: '处理中', icon: 'el-icon-edit', tone: 'warning' },
        { value: summary.defectOpen, label: '不良品未处理', icon: 'el-icon-warning', tone: 'warning' },
        {
          value: formatLiveRateText(summary.replyOntime),
          label: '回复及时率',
          icon: 'el-icon-time',
          tone: 'success',
        },
        {
          value: formatLiveRateText(summary.defectOntime),
          label: '不良品及时率',
          icon: 'el-icon-circle-check',
          tone: 'success',
        },
        {
          value: formatLiveRateText(summary.closeOntime),
          label: '结案及时率',
          icon: 'el-icon-finished',
          tone: 'info',
        },
      ];
    },
    pagination() {
      const total = filterLiveRows(this.rows, this.filters.q).length;
      return {
        page: this.page,
        pageSize: this.pageSize,
        total,
        layout: 'total, sizes, prev, pager, next',
        pageSizes: [30, 50, 100],
        onPageChange: (page: number) => {
          this.page = page;
        },
        onSizeChange: (size: number) => {
          this.pageSize = size;
          this.page = 1;
        },
      };
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      if (!peekLiveCasesCache()) this.loading = true;
      fetchLiveCases()
        .then((rows) => {
          this.rows = rows;
          this.error = '';
        })
        .catch((err: Error) => {
          this.error = err.message;
        })
        .then(() => {
          this.loading = false;
        });
    },
    onFiltersInput(value: LiveDetailFilters) {
      this.filters = value;
      this.page = 1;
    },
    openRow(row: LiveCase) {
      this.activeRow = row;
      this.drawerOpen = true;
    },
    onSaved(row: LiveCase) {
      const index = this.rows.findIndex((item) => item.id === row.id);
      if (index >= 0) this.$set(this.rows, index, row);
      this.activeRow = row;
    },
  },
});
</script>

<style lang="scss" scoped>
.live-detail-page {
  @include list-page-layout;
}

.live-detail-page__kpis {
  flex-shrink: 0;
}

.err {
  color: $color-danger;
}

.list-page-crud ::v-deep .el-table__row {
  cursor: pointer;
}
</style>
